// RealEstateInfo.tsx
import React, {useEffect, useRef, useState} from 'react';
import {Blacked} from '../../../../CRM/components/styles';
import {ReactComponent as ModalCloseIcon} from '../../../../assets/HomeTownIcon/ModalCloseIcon.svg';
import styled from 'styled-components';
import {Input, message, Image} from 'antd';
import {useAdvertiserAgentIDList} from '../../../hooks/Advertiser/CustomerCommercial/GetAdvertiserAgentIDList';
import {MEDIA_URL} from '../../../../app/constants/APIKEYS';


export const RealEstateInfo = ({setRealEstateId, realEstateId}) => {
    // 유틸리티
    const [storeLocation, setStoreLocation] = useState<{ lat: number; lng: number} | null>(null);
    const [isPreviewModal, setIsPreviewModal] = React.useState(false);
    const [mapReady, setMapReady] = useState<boolean>(() => !!window?.naver?.maps);
    const [mapLoadFailed, setMapLoadFailed] = useState(false);

    // 데이터 조회
    const {advertiserAgentIDList} = useAdvertiserAgentIDList(realEstateId);

    // 지도/마커 참조
    const mapContainerRef = useRef<HTMLDivElement | null>(null);
    const mapRef = useRef<any>(null);
    const markerRef = useRef<any>(null);

    // 네이버맵 로딩 상태 확인
    useEffect(() => {
        let cancelled = false;
        let rafId: number | null = null;
        let timer: number | null = null;

        const loop = () => {
            if (cancelled) return;
            if (window.naver?.maps) {
                setMapReady(true);
                setMapLoadFailed(false);
                return;
            }
            rafId = requestAnimationFrame(loop);
        };

        if (!mapReady) {
            loop();
            timer = window.setTimeout(() => {
                if (!window.naver?.maps && !cancelled) {
                    setMapLoadFailed(true);
                    if (rafId) cancelAnimationFrame(rafId);
                }
            }, 4000);
        }

        return () => {
            cancelled = true;
            if (rafId) cancelAnimationFrame(rafId);
            if (timer) clearTimeout(timer);
        };
    }, [mapReady]);

    // 지도 초기화 (스크립트는 외부에서 로드되어 있다고 가정)
    useEffect(() => {
        if(!storeLocation) return;
        let mounted = true;

        if (!mapReady || mapLoadFailed) return;
        if (!mapContainerRef.current) return;
        if (mapRef.current) return;

        const init = () => {
            try {
                if (!mounted || !window.naver?.maps) return;
                const {naver} = window;

                mapRef.current = new naver.maps.Map(mapContainerRef.current, {
                    center: new naver.maps.LatLng(storeLocation.lat, storeLocation.lng),
                    zoom: 12,
                });

                // 초기 마커
                markerRef.current = new naver.maps.Marker({
                    position: new naver.maps.LatLng(storeLocation.lat, storeLocation.lng),
                    map: mapRef.current,
                });
            } catch (err) {
                console.error('네이버 지도 초기화 실패', err);
                setMapLoadFailed(true);
            }
        };

        if (window.naver?.maps) {
            init();
        } else {
            // 외부에서 커스텀 이벤트를 쏘지 않는 경우를 대비해 rAF 폴링
            const tick = () => {
                if (!mounted) return;
                if (window.naver?.maps) init();
                else requestAnimationFrame(tick);
            };
            requestAnimationFrame(tick);
        }

        return () => {
            mounted = false;
            if (markerRef.current) {
                markerRef.current.setMap(null);
                markerRef.current = null;
            }
            // mapRef는 SDK가 정리
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [storeLocation, mapLoadFailed, mapReady]);

    // 주소 기반 좌표 세팅
    const setLocation = (fullAddress: string) => {
        const {naver} = window;
        if (!naver?.maps || !mapReady || mapLoadFailed) return;
        if (!fullAddress) return;

        if (!naver?.maps?.Service?.geocode) {
            console.error('네이버 지오코더가 준비되지 않았습니다.');
            return;
        }

        naver.maps.Service.geocode(
            {query: fullAddress},
            (status: any, response: any) => {
                if (status === naver.maps.Service.Status.OK) {
                    const result = response?.v2?.addresses?.[0];
                    const lat = parseFloat(result?.y);
                    const lng = parseFloat(result?.x);
                    if (!isNaN(lat) && !isNaN(lng)) {
                        setStoreLocation({lat, lng});

                        // 지도/마커 반영
                        if (mapRef.current && naver?.maps?.LatLng) {
                            const pos = new naver.maps.LatLng(lat, lng);
                            mapRef.current.setCenter(pos);
                            mapRef.current.setZoom(12);

                            if (!markerRef.current) {
                                markerRef.current = new naver.maps.Marker({position: pos, map: mapRef.current});
                            } else {
                                markerRef.current.setPosition(pos);
                                if (!markerRef.current.getMap()) markerRef.current.setMap(mapRef.current);
                            }
                        }
                    }
                } else {
                    console.error('Geocoding failed:', response);
                    message.error('해당 주소는 검색할 수 없습니다.');
                }
            }
        );
    };

    // 데이터 로드되면 주소로 이동
    useEffect(() => {
        if (advertiserAgentIDList?.address) {
            setLocation(advertiserAgentIDList.address);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [advertiserAgentIDList?.address]);

    // 디버그
    // console.log('agent detail', advertiserAgentIDList);

    return (
        <>
            <div>
                {advertiserAgentIDList && (
                    <div>
                        <Blacked onClick={() => setRealEstateId(null)}/>
                        <ModalWrapper className="scroll-container">
                            <ModalHeader>
                                <ModalTitle>{'부동산중개사무소 정보'}</ModalTitle>
                                <CloseIconSVG as={ModalCloseIcon} onClick={() => setRealEstateId(null)}/>
                            </ModalHeader>

                            <MapWrapper>
                                {/* 공식 SDK로 직접 렌더링 */}
                                <MapArea>
                                    <div style={{width: '100%', height: '100%'}} ref={mapContainerRef}/>
                                    {!mapReady && !mapLoadFailed && (
                                        <MapStatus>지도를 불러오는 중입니다…</MapStatus>
                                    )}
                                    {mapLoadFailed && (
                                        <MapStatus>지도 로딩에 실패했습니다. 새로고침 후 다시 시도해주세요.</MapStatus>
                                    )}
                                </MapArea>
                            </MapWrapper>

                            <Table>
                                <tbody>
                                <TableRow>
                                    <Th>
                                        <ThLabel>상호명</ThLabel>
                                    </Th>
                                    <Td>{advertiserAgentIDList?.store}</Td>
                                    <Th>
                                        <ThLabel>대표자</ThLabel>
                                    </Th>
                                    <Td>{advertiserAgentIDList?.name}</Td>
                                </TableRow>

                                <TableRow>
                                    <Th>
                                        <ThLabel>주소</ThLabel>
                                    </Th>
                                    <Td colSpan={3}>{advertiserAgentIDList?.address}</Td>
                                </TableRow>

                                <TableRow>
                                    <Th>
                                        <ThLabel>매체정보</ThLabel>
                                    </Th>
                                    <Td colSpan={3}>
                                        {`${advertiserAgentIDList?.mediaInfo || '-'}, ${advertiserAgentIDList?.mediaType || '-'}`}
                                    </Td>
                                </TableRow>

                                <TableRow>
                                    <Th>
                                        <ThLabel>매체 운영시간</ThLabel>
                                    </Th>
                                    <Td colSpan={3}>{advertiserAgentIDList?.businessHours?.replace(/,/g, '') || '-'}</Td>
                                </TableRow>

                                <TableRow>
                                    <Th>
                                        <ThLabel>사진</ThLabel>
                                    </Th>
                                    <Td colSpan={3}>
                                        {advertiserAgentIDList?.fileList?.filter((item: any) => item?.type === 'IMG')?.length ? (
                                            advertiserAgentIDList.fileList
                                                .filter((item: any) => item?.type === 'IMG')
                                                .map((item: any, index: number) => (
                                                    <Image
                                                        key={index}
                                                        alt="advertiserAgentIDList_img"
                                                        style={{width: 130, margin: '0 2px'}}
                                                        src={MEDIA_URL + item?.url}
                                                    />
                                                ))
                                        ) : (
                                            '-' // 이미지 없을 때
                                        )}
                                    </Td>
                                </TableRow>

                                <TableRow>
                                    <Th>
                                        <ThLabel>유동인구 및 상권</ThLabel>
                                    </Th>
                                    <Td colSpan={3}>
                                        <div>{`일평균 유동인구 등급: ${advertiserAgentIDList?.footTrafficGrade ?? ''}`}</div>

                                        <GraphWrapper>
                                            {advertiserAgentIDList?.fileList?.find((item: any) => item?.type === 'FT') ? (
                                                <Image
                                                    alt="FT_img"
                                                    style={{width: '100%', margin: '0 2px'}}
                                                    src={
                                                        MEDIA_URL +
                                                        advertiserAgentIDList?.fileList?.find((item: any) => item?.type === 'FT')?.url
                                                    }
                                                />
                                            ) : (
                                                '-'
                                            )}
                                        </GraphWrapper>

                                        <GraphWrapper>
                                            {advertiserAgentIDList?.fileList?.find((item: any) => item?.type === 'F') && (
                                                <Image
                                                    alt="F_img"
                                                    style={{width: '100%', margin: '0 2px'}}
                                                    src={
                                                        MEDIA_URL +
                                                        advertiserAgentIDList?.fileList?.find((item: any) => item?.type === 'F')?.url
                                                    }
                                                />
                                            )}
                                        </GraphWrapper>
                                    </Td>
                                </TableRow>
                                </tbody>
                            </Table>

                            <ButtonsWrapper>{/* 버튼 영역 필요 시 복구 */}</ButtonsWrapper>
                        </ModalWrapper>
                    </div>
                )}
            </div>
        </>
    );
};

/* ===================== styled ===================== */

const ButtonsWrapper = styled.div`
    margin-top: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
`;

const RegistButton = styled.div`
    width: 100px;
    height: 40px;
    padding: 11px;
    color: white;
    background-color: #530dad;
    border-radius: 2px;
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;

    font-weight: 500;
    font-size: 15px;
    line-height: 17.9px;
    font-family: Pretendard, sans-serif;
`;

const PrevNextText = styled.div`
    font-weight: 400;
    font-size: 14px;
    line-height: 16.71px;
    font-family: Pretendard, sans-serif;
    color: #00000099;
    cursor: pointer;
`;

const ArrowSVG = styled.svg`
    width: 20px;
`;

const GraphWrapper = styled.div``;

const GraphContents = styled.div`
    display: flex;
    align-items: center;
`;

const LabelContents = styled.div`
    width: 100px;
`;

const LabelTitle = styled.div`
    font-size: 12px;
    font-weight: 600;
    margin-bottom: 8px;
`;

const ColorBadge = styled.div<{ color: string }>`
    background-color: ${(props) => props.color};
    width: 7px;
    height: 7px;
    border-radius: 50px;
    margin-right: 4px;
`;

const LabelText = styled.div`
    display: flex;
    font-size: 11px;
    align-items: center;
    margin-bottom: 4px;
`;

const LabelValue = styled.div`
    font-weight: 600;
    margin-left: 10px;
`;

const GraphTitle = styled.div`
    display: flex;
    gap: 10px;
    align-items: center;
    font-weight: 600;
`;

const GraphSubTitle = styled.div`
    color: #00000099;
    font-size: 14px;
`;

const MapWrapper = styled.div``;

const ModalWrapper = styled.div`
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    max-height: 900px;
    width: 600px;
    min-height: 411px;
    background-color: #fff;
    overflow-y: auto;
    border-radius: 8px;
    padding: 24px;
    z-index: 99999;
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const ModalHeader = styled.div`
    display: flex;
    justify-content: space-between;
    width: 100%;
    margin-bottom: 21.5px;
`;

const CloseIconSVG = styled.svg`
    width: 24px;
    cursor: pointer;
`;

const MapArea = styled.div`
    position: relative;
    width: 532px;
    height: 295px;
`;

const MapStatus = styled.div`
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 12px;
    text-align: center;
    background: rgba(255, 255, 255, 0.85);
    color: #111;
    font-weight: 600;
    border-radius: 4px;
`;

const ModalTitle = styled.div`
    font-family: Pretendard, sans-serif;
    font-weight: 600;
    font-size: 18px;
    line-height: 21.48px;
    color: #2e2559;
`;

const Table = styled.table`
    width: 100%;
    margin-top: 8px;
    border-collapse: collapse;
    border: 1px solid #ccc;
`;

const TableRow = styled.tr`
    min-height: 40px;
`;

const Th = styled.th`
    padding: 10px;
    text-align: left;
    width: 20%;
    border: 1px solid #ccc;
    background-color: #f4f2ff;
    vertical-align: middle;
`;

const ThLabel = styled.div`
    font-family: Pretendard, sans-serif;
    font-weight: 500;
    font-size: 14px;
    line-height: 16.71px;
    color: #00000099;
    display: flex;
    gap: 2px;
`;

const RequireIcon = styled.p`
    color: #f15151;
`;

const Td = styled.td`
    padding: 10px;
    text-align: left;
    border: 1px solid #ccc;
`;

const InputText = styled(Input)`
    width: 100%;
    height: 28px;
    padding-left: 10px;
    border: 1px solid #2e2559;
    border-radius: 4px;
    display: flex;
`;

const CancelButton = styled.div`
    width: 100px;
    padding: 11px;
    border: 1px solid #000000;
    border-radius: 2px;
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;

    font-weight: 500;
    font-size: 15px;
    line-height: 17.9px;
    font-family: Pretendard, sans-serif;
`;
